import Chart from 'chart.js/auto';
import './components/calculators/format';
import './components/calculators/input';
import './components/calculators/retirement';

// =====================================================================
//  RETIREMENT — Accumulation Plan calculator (interactivity layer)
//
//  Single-page, live-updating version of the PPA/PRS "How much do you
//  need to save for retirement?" flow. All math lives in
//  Calc.retirement (components/calculators/retirement.js); this file
//  only reads the form, calls Calc.retirement.compute(), and paints the
//  results / chart / table.
// =====================================================================
(function () {
  const fmt = Calc.format;
  const R = Calc.retirement;

  const money = (n) => fmt.money(n);                    // "RM 1,234.00" (cents — detailed figures)
  const money0 = (n) => fmt.money(n, 0);                // "RM 1,234" (rounded — stat band + chart)
  // iMoney brand palette for the projection chart (see _dev/css/partials/_variables.scss)
  const BRAND = {
    epf: '#0009AA',        // $primary  ($color-base1-medium)
    personal: '#00C8F2',   // $color-base2-light (brand cyan)
    remaining: '#CBDAFE',  // $color-base1-lightest (light periwinkle)
    target: '#05E399',     // $success  ($color-base3, brand green)
  };

  // Current target value the dashed reference line should sit at. Kept in
  // this scope (not the plugin's closure) so slider updates move the line —
  // the plugin instance is registered once but reads this live.
  let chartTarget = 0;
  const targetLinePlugin = {
    id: 'rcTargetLine',
    afterDatasetsDraw(c) {
      const y = c.scales.y.getPixelForValue(chartTarget);
      if (!isFinite(y)) return;
      const { left, right } = c.chartArea;
      const ctx = c.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = BRAND.target;
      ctx.moveTo(left, y); ctx.lineTo(right, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = BRAND.target;
      ctx.font = '600 12px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('Target: ' + money0(chartTarget), right - 4, y - 6);
      ctx.restore();
    },
  };

  // Working state, seeded from the module defaults.
  const state = {
    age_now: 30,
    age_retire: 60,
    age_end: 85,
    spend_month_today: 3000,
    net_now_ex_epf: 20000,
    epf_total_now: 50000,
    salary_gross_month: 5000,
    epf_employee_pct: 11,
    epf_employer_pct: 13,
    salary_growth_apy: 3,
    personal_contrib_pct: 0,
    annual_topup: { amount: 0, month: 12 },
    swingView: 'some',
    maxDrop: '10',
    activeTab: 'chart',
  };
  const assumptions = { cpi: 0.03, epf_nominal: 0.05, real_return_for_pot: 0.01 };

  let chart = null;

  const pctNum = (r) => (r * 100).toFixed(1);   // "9.2" — the % lives in the HTML

  // --- tiny DOM helpers -------------------------------------------------
  const setText = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  const syncInput = (id, val) => Calc.input.syncValue(id, val);
  // Write a value into every placeholder span with this class (text is in
  // the HTML — JS only fills the number).
  const setValAll = (cls, val) => { document.querySelectorAll('.' + cls).forEach(el => { el.textContent = val; }); };
  // Show exactly the elements whose data-<attr> equals `value`; hide siblings.
  // Lets us pick one of several pre-written copy variants without authoring
  // any text in JS.
  const showByData = (attr, value) => {
    document.querySelectorAll('[data-' + attr + ']').forEach(el => {
      el.classList.toggle('d-none', el.getAttribute('data-' + attr) !== value);
    });
  };

  // --- read the form into `state` --------------------------------------
  function readForm() {
    const get = (id) => Calc.input.getValue(document.getElementById(id));
    state.age_now = get('rc-age-now') || 0;
    state.age_retire = get('rc-age-retire') || 0;
    state.age_end = get('rc-age-end') || 0;
    state.spend_month_today = get('rc-spend') || 0;
    state.net_now_ex_epf = get('rc-net-now') || 0;
    state.epf_total_now = get('rc-epf-now') || 0;
    state.salary_gross_month = get('rc-salary') || 0;
    state.epf_employee_pct = get('rc-epf-emp') || 0;
    state.epf_employer_pct = get('rc-epf-empr') || 0;
    state.salary_growth_apy = get('rc-salary-growth') || 0;
    state.personal_contrib_pct = get('rc-personal-pct') || 0;
    state.annual_topup.amount = get('rc-topup-amount') || 0;
    const monthEl = document.getElementById('rc-topup-month');
    state.annual_topup.month = monthEl ? parseInt(monthEl.value, 10) || 12 : 12;
    assumptions.cpi = (get('rc-cpi') || 0) / 100;
    assumptions.epf_nominal = (get('rc-epf-rate') || 0) / 100;
  }

  // Push state back into the controls (after a shortfall action mutates it).
  function writeForm() {
    syncInput('rc-age-retire', state.age_retire);
    syncInput('rc-spend', state.spend_month_today);
    syncInput('rc-personal-pct', +state.personal_contrib_pct.toFixed(1));
  }

  // Restore every control to the module defaults, then recompute.
  function resetForm() {
    const d = R.defaults;
    syncInput('rc-age-now', d.age_now);
    syncInput('rc-age-retire', d.age_retire);
    syncInput('rc-age-end', d.age_end);
    syncInput('rc-spend', d.spend_month_today);
    syncInput('rc-net-now', d.net_now_ex_epf);
    syncInput('rc-epf-now', d.epf_total_now);
    syncInput('rc-salary', d.salary_gross_month);
    syncInput('rc-epf-emp', d.epf_employee_pct);
    syncInput('rc-epf-empr', d.epf_employer_pct);
    syncInput('rc-salary-growth', d.salary_growth_apy);
    syncInput('rc-personal-pct', d.personal_contrib_pct);
    syncInput('rc-topup-amount', d.annual_topup.amount);
    const m = document.getElementById('rc-topup-month');
    if (m) m.value = String(d.annual_topup.month);
    syncInput('rc-cpi', R.assumptions.cpi * 100);
    syncInput('rc-epf-rate', R.assumptions.epf_nominal * 100);
    // Risk pills back to defaults
    document.querySelectorAll('.risk-pills').forEach(group => {
      const def = group.dataset.risk === 'swingView' ? d.swingView : d.maxDrop;
      group.querySelectorAll('.nav-link').forEach(b => b.classList.toggle('active', b.dataset.value === def));
    });
    state.swingView = d.swingView;
    state.maxDrop = d.maxDrop;
    refresh();
  }

  // Basic ordering guard — surface a hint but still compute so the user
  // always sees a live result (Calc.retirement clamps negative spans to 0).
  function ageWarning() {
    const el = document.getElementById('rc-age-warning');
    if (!el) return;
    let key = 'none';
    if (state.age_retire <= state.age_now) key = 'retire';
    else if (state.age_end <= state.age_retire) key = 'end';
    el.classList.toggle('d-none', key === 'none');
    showByData('agewarn', key);   // both message variants live in the HTML

    // Flag the offending input in red so the mistake is obvious.
    const invalid = (id, on) => {
      const el = document.getElementById(id);
      if (el) el.classList.toggle('rc-invalid', on);
    };
    invalid('rc-age-retire', key === 'retire');
    invalid('rc-age-end', key === 'end');
  }

  // ---------------------------------------------------------------------
  function refresh() {
    readForm();
    ageWarning();

    const r = R.compute(state, assumptions);

    // When the target is out of reach, the solver's required return is
    // unrealistic (capped high), so its projection nearly hits the target and
    // hides the gap. Re-project at the portfolio's realistic max return so the
    // "Projected", progress bar, chart and table show the true shortfall.
    if (r.notAchievable) {
      const realistic = r.maxReturn;
      r.breakdown = R.breakdown(r.inputs, r.assumptions, realistic);
      r.projectedHave = r.breakdown.epf.total + r.breakdown.personal.total;
      r.series = R.onTrackSeries(r.inputs, r.assumptions, realistic, r.target);
      r.detailed = R.detailedSeries(r.inputs, r.assumptions, realistic);
      r.ageTargetMet = R.ageTargetMet(r.series, r.target);
    }

    // Live derived helpers
    setValAll('rc-v-years', Math.max(0, state.age_retire - state.age_now));
    setValAll('rc-personal-pct-val', state.personal_contrib_pct.toFixed(1));
    setText('rc-personal-amount', money(state.salary_gross_month * (state.personal_contrib_pct / 100)));
    setText('rc-cur-cpi', (assumptions.cpi * 100).toFixed(1));
    setText('rc-cur-epf', (assumptions.epf_nominal * 100).toFixed(1));

    // Stat band — rounded (no cents) to stay compact
    setText('rc-stat-target', money0(r.target));
    setText('rc-stat-projected', money0(r.projectedHave));

    // Key figures (numeric values only)
    setText('rc-r-target', money(r.target));
    setText('rc-r-projected', money(r.projectedHave));
    setText('rc-r-salary', money(r.salaryAtRet));
    setText('rc-r-epf-total', money(r.breakdown.epf.total));
    setText('rc-r-epf-prin', money(r.breakdown.epf.principal));
    setText('rc-r-epf-grow', money(r.breakdown.epf.interest));
    setText('rc-r-pers-total', money(r.breakdown.personal.total));
    setText('rc-r-pers-prin', money(r.breakdown.personal.principal));
    setText('rc-r-pers-grow', money(r.breakdown.personal.interest));

    renderSummary(r);
    if (state.activeTab === 'chart') renderChart(r); else renderTable(r);
  }

  // --- plan summary -----------------------------------------------------
  // Toggles which pre-written copy variant shows and fills the .rc-v-* value
  // spans. No sentences are authored here — all copy lives in the template
  // so it is server-rendered and crawlable.
  function renderSummary(r) {
    const surplus = Math.max(0, r.projectedHave - r.target);
    const shortamt = Math.max(0, r.target - r.projectedHave);
    const progresspct = r.target > 0 ? Math.min(100, Math.round((r.projectedHave / r.target) * 100)) : 100;
    // ontrack = no extra return needed · reach = attainable within portfolio · short = beyond it
    const verdict = r.onTrack ? 'ontrack' : (r.notAchievable ? 'short' : 'reach');

    // Value spans (numbers only; units live in the HTML)
    setValAll('rc-v-target', money(r.target));
    setValAll('rc-v-projected', money(r.projectedHave));
    setValAll('rc-v-surplus', money(surplus));
    setValAll('rc-v-shortamt', money(shortamt));
    setValAll('rc-v-progresspct', progresspct);
    setValAll('rc-v-return', pctNum(r.requiredReturn));
    setValAll('rc-v-maxreturn', pctNum(r.maxReturn));
    setValAll('rc-v-cpi', (assumptions.cpi * 100).toFixed(1));
    setValAll('rc-v-epf', (assumptions.epf_nominal * 100).toFixed(1));

    // Pick the pre-written variants
    showByData('verdict', verdict);                       // badge + explanation
    showByData('ret', r.onTrack ? 'ontrack' : 'value');   // stat-tile: "On track" vs a %
    showByData('fund', r.fundType);                       // name + description everywhere

    // Progress bar (width via style, colour via class — no text authored here)
    const bar = document.getElementById('rc-progress-bar');
    if (bar) {
      bar.style.width = progresspct + '%';
      bar.className = 'progress-bar rc-progress-bar rc-progress-bar--' + verdict;
    }

    // Warn colour on the required-return figures (class toggle only)
    document.querySelectorAll('.rc-v-return').forEach(el => el.classList.toggle('rc-return-warn', r.notAchievable));

    // Shortfall action buttons appear only when the target is out of reach
    document.getElementById('rc-shortfall').classList.toggle('d-none', !r.notAchievable);
  }

  // --- projection chart -------------------------------------------------
  function renderChart(r) {
    const canvas = document.getElementById('rc-chart');
    if (!canvas) return;
    const labels = r.series.map(row => row.age);
    const epf = r.series.map(row => Math.round(row.epf));
    const personal = r.series.map(row => Math.round(row.personal));
    const remaining = r.series.map(row => Math.round(row.remaining));
    chartTarget = Math.round(r.target);

    const data = {
      labels,
      datasets: [
        { label: 'EPF', data: epf, backgroundColor: 'rgba(0,9,170,0.55)', borderColor: BRAND.epf, borderWidth: 1, fill: true, pointRadius: 0, tension: 0.25 },
        { label: 'Personal Investment', data: personal, backgroundColor: 'rgba(0,200,242,0.55)', borderColor: BRAND.personal, borderWidth: 1, fill: true, pointRadius: 0, tension: 0.25 },
        { label: 'Balance to Target', data: remaining, backgroundColor: 'rgba(203,218,254,0.55)', borderColor: BRAND.remaining, borderWidth: 1, fill: true, pointRadius: 0, tension: 0 },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { stacked: true, title: { display: false }, ticks: { callback: (v, i) => 'Age ' + labels[i], maxTicksLimit: 10 }, grid: { display: false } },
        y: { stacked: true, ticks: { callback: (v) => 'RM ' + Number(v).toLocaleString('en-MY') } },
      },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: { callbacks: { title: (items) => 'Age ' + labels[items[0].dataIndex], label: (ctx) => ctx.dataset.label + ': ' + money(ctx.raw) } },
      },
    };

    if (chart) {
      chart.data = data;
      chart.options = options;
      chart.update();
    } else {
      chart = new Chart(canvas.getContext('2d'), { type: 'line', data, options, plugins: [targetLinePlugin] });
    }
  }

  // --- year-by-year table ----------------------------------------------
  function renderTable(r) {
    const body = document.getElementById('rc-table-body');
    if (!body) return;
    body.innerHTML = r.detailed.map(row => `
      <tr>
        <td>${row.age}</td>
        <td>${money(row.epfBeginning)}</td>
        <td>${money(row.epfContributions)}</td>
        <td>${money(row.epfGrowth)}</td>
        <td>${money(row.epfEnding)}</td>
        <td>${money(row.personalBeginning)}</td>
        <td>${money(row.personalContributions)}</td>
        <td>${money(row.personalGrowth)}</td>
        <td>${money(row.personalEnding)}</td>
        <td>${money(row.totalEnding)}</td>
      </tr>`).join('');
  }

  // --- tabs -------------------------------------------------------------
  function activateTab(tabId) {
    state.activeTab = tabId;
    document.querySelectorAll('#rc-tabs .btn-tab').forEach(btn => btn.classList.toggle('active', btn.dataset.tab === tabId));
    document.querySelectorAll('#rc-tab-chart, #rc-tab-table').forEach(p => p.classList.toggle('d-none', p.id !== `rc-tab-${tabId}`));
    refresh();
  }

  // --- events -----------------------------------------------------------
  function bindEvents() {
    // Keep the un-masked percent fields numeric — digits and a single dot only.
    // (Money/age fields already use Inputmask; the personal % is a slider.)
    ['rc-cpi', 'rc-epf-rate', 'rc-epf-emp', 'rc-epf-empr', 'rc-salary-growth'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', () => {
        let v = el.value.replace(/[^\d.]/g, '');
        const parts = v.split('.');
        if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
        if (v !== el.value) el.value = v;
      });
    });

    const inputIds = ['rc-age-now', 'rc-age-retire', 'rc-age-end', 'rc-spend', 'rc-net-now', 'rc-epf-now',
      'rc-salary', 'rc-epf-emp', 'rc-epf-empr', 'rc-salary-growth', 'rc-personal-pct', 'rc-topup-amount',
      'rc-topup-month', 'rc-cpi', 'rc-epf-rate'];
    inputIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', refresh);
      el.addEventListener('change', refresh);
    });

    // Select-all on focus for the text/number fields — quicker to overwrite.
    ['rc-age-now', 'rc-age-retire', 'rc-age-end', 'rc-spend', 'rc-net-now', 'rc-epf-now',
      'rc-salary', 'rc-epf-emp', 'rc-epf-empr', 'rc-salary-growth', 'rc-topup-amount',
      'rc-cpi', 'rc-epf-rate'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('focus', () => setTimeout(() => { try { el.select(); } catch (e) {} }, 0));
    });

    // Reset to defaults
    const resetBtn = document.getElementById('rc-reset');
    if (resetBtn) resetBtn.addEventListener('click', resetForm);

    // Risk pill groups
    document.querySelectorAll('.risk-pills').forEach(group => {
      const key = group.dataset.risk;
      group.querySelectorAll('.nav-link').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.nav-link').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          state[key] = btn.dataset.value;
          refresh();
        });
      });
    });

    // Result tabs
    document.querySelectorAll('#rc-tabs .btn-tab').forEach(btn =>
      btn.addEventListener('click', () => activateTab(btn.dataset.tab)));

    // Shortfall actions
    document.querySelectorAll('#rc-shortfall [data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const act = btn.dataset.action;
        if (act === 'save' && state.salary_gross_month > 0) {
          state.personal_contrib_pct = Math.min(50, state.personal_contrib_pct + (500 / state.salary_gross_month) * 100);
        } else if (act === 'delay') {
          state.age_retire = Math.min(75, state.age_retire + 2);
        } else if (act === 'spend') {
          state.spend_month_today = Math.max(0, state.spend_month_today - 500);
        }
        writeForm();
        refresh();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindEvents();
    refresh();
  });
})();
